import { handleError } from "@/api/handleError";
import {
  emailLogin,
  passwordResetConfirm,
  passwordResetRequest,
  sendVerificationEmail,
} from "@/api/requests/auth";
import { getEncryptionKey } from "@/api/requests/misc";
import { getTeamMemberV2 } from "@/api/requests/teamMembers";
import { getUserV2 } from "@/api/requests/users";
import { useAuthStore } from "@/stores/auth.store";
import { useBusinessStore } from "@/stores/businesses.store";
import { useTokenStore } from "@/stores/token.store";
import { useUsersStore } from "@/stores/users.store";
import { notify } from "@kyvg/vue3-notification";
import { useRouter } from "vue-router";
import requestClient from "./api/requestClient";
import { encryptToken } from "./encryptionKeys";
import { trackAmplitudeEvent } from "./integrations/analytics/amplitude";
import { AMPLITUDE_EVENTS } from "./integrations/analytics/events";
import { updateFeedback } from "./queries";
import { validateMobile } from "./utils";

export const findRecommendedPlan = (
  teamMembers?: number | string | null,
  isInternational?: boolean | null,
  billingPeriod?: string | null
) => {
  if (isInternational) {
    if (billingPeriod == "Quarterly") {
      return "International (Quarterly)";
    } else if (billingPeriod == "Annually") {
      return "International (Annual)";
    }
    return "International Plan";
  } else {
    if (teamMembers && Number(teamMembers) >= 1 && Number(teamMembers) <= 3) {
      if (billingPeriod == "Quarterly") {
        return "Standard Plan (Quarterly)";
      }
      if (billingPeriod == "Annually") {
        return "Standard Plan (Annual)";
      }

      return "Standard Plan";
    } else if (teamMembers && Number(teamMembers) > 3) {
      if (billingPeriod == "Quarterly") {
        return "Growth Plan (Quarterly)";
      }
      if (billingPeriod == "Annually") {
        return "Growth Plan (Annual)";
      }
      return "Growth Plan";
    }
  }
};

export const handleFetchUserDetails = async () => {
  const userStore = useUsersStore();
  const businessStore = useBusinessStore();

  try {
    const response = await getUserV2();

    userStore.setCurrentUser({
      ...userStore.currentUserBusinessLevel,
      ...response.data,
    });

    await Promise.all([
      userStore.fetchUserFreeTrialData(),
      businessStore.getBusiness(),
    ]);

    userStore.user_role = businessStore.activeProfile?.profile?.role;
  } catch (error) {
    handleError({
      error,
      fallbackMessage: "Error while Fetching User details",
    });
  }
};

export const updateFeedbackSheet = async () => {
  await updateFeedback();
};

export const getPermissions = async () => {
  const businessStore = useBusinessStore();
  const userId = businessStore.currentUserId;
  const businessId = businessStore.currentBusinessId;

  try {
    if (!userId || !businessId) {
      throw new Error("User ID or Business ID not found");
    }

    const res = await getTeamMemberV2({
      businessId,
      userId,
    });
    return res.data.permission;
  } catch (error) {
    handleError({
      error,
      fallbackMessage: "Unable to get permissions",
      silent: true,
    });
  }
};

type PartnerQueryParams = {
  id?: string;
  trxref?: string;
  ref?: string;
  code?: string;
  mobile?: string;
};

type QueryParamsKeys = keyof PartnerQueryParams;

type PartnerPayload = { mobile?: string; scheme?: string };

export const handlePartnerSignIn = async (
  callback: (data: PartnerPayload) => Promise<void>
) => {
  const authStore = useAuthStore();
  const router = useRouter();
  try {
    const queryString = window.location.href.split("?")[1];

    if (!queryString) {
      return;
    }

    const paramsArray = queryString.split("&");

    const queryParams: PartnerQueryParams = {};

    for (const param of paramsArray) {
      const [key, value] = param.split("=");

      queryParams[key as QueryParamsKeys] = decodeURIComponent(
        value.replace(/\+/g, " ")
      );
    }

    let payload: PartnerPayload = {
      scheme: "SMS",
    };

    if (queryParams.id) {
      payload = {
        mobile: validateMobile(queryParams.id),
      };
    }

    if (queryParams.trxref && authStore.mobile) {
      // update the payload with mobile number from signup page if redirected from paystack
      payload.mobile = authStore.mobile;
    }

    if (payload.mobile) {
      authStore.mobile = payload.mobile;
    }

    if (queryParams?.ref === "partner") {
      if (queryParams?.code === "WGH") {
        void trackAmplitudeEvent(
          AMPLITUDE_EVENTS.WGH_REFERALL_SIGNUP,
          queryParams
        );
      }
    }

    if (queryParams?.ref === "go-global-signin" && authStore.mobile) {
      payload.mobile = authStore.mobile;
    }

    await callback(payload);
  } catch (error) {
    handleError({ error, fallbackMessage: "Error on signing in partners" });

    await router.push("/sign-in");
  }
};

export function removeDialCode(phoneNumber: string) {
  // Define an array of common international dial codes
  const dialCodes = ["+234", "+1", "+254", "234", "1", "254"];
  // Loop through the dial codes and check if the phone number starts with any of them
  for (const code of dialCodes) {
    if (phoneNumber.startsWith(code)) {
      // If a dial code is found, return the phone number with the dial code removed
      return phoneNumber.slice(code.length);
    }
  }
  // If no dial code is found, return the original phone number
  return phoneNumber;
}

export const validateAuthInput = (input: string) => {
  if (!input) return false;
  const text = input.trim();
  if (text.length < 1) {
    return false;
  }

  if (text.replace(/[^a-zA-Z]/g, "").length < 1) {
    return false;
  }

  return true;
};
export const sendVerifyEmail = async (email: string, resend = false) => {
  const authStore = useAuthStore();
  const isValidEmail = /.+@.+\..+/.test(email);
  if (!isValidEmail) {
    notify({
      type: "error",
      title: "Invalid Email",
      text: "Please enter a valid email address",
    });
    return;
  }
  try {
    authStore.loading = true;

    await sendVerificationEmail({ emailInputSchema: { email } });

    if (resend) {
      notify({
        type: "success",
        title: "Email Resent",
        text: "Verification email has been resent",
      });
    }
  } catch (error) {
    const handledError = handleError({
      error,
      fallbackMessage: "An error occurred with sending verification email",
    });

    if (handledError?.message?.toLowerCase().includes("already verified")) {
      return {
        redirect: "/sign-in",
      };
    }
  } finally {
    authStore.loading = false;
  }
};
export const sendResetVerifyEmail = async (email: string, resend = false) => {
  const authStore = useAuthStore();

  const isValidEmail = /.+@.+\..+/.test(email);

  if (!isValidEmail) {
    notify({
      type: "error",
      title: "Invalid Email",
      text: "Please enter a valid email address",
    });
    return;
  }

  try {
    authStore.loading = true;

    await passwordResetRequest({ emailInputSchema: { email } });

    authStore.resetPasswordData.email = email;

    if (resend) {
      notify({
        type: "success",
        title: "Email Resent",
        text: "Verification email has been resent",
      });
    }
  } catch (error) {
    handleError({
      error,
      fallbackMessage: "An error occurred with sending verification email",
    });
  } finally {
    authStore.loading = false;
  }
};
export const handleConfirmPasswordReset = async (
  newPassword: string,
  id: string,
  token: string
) => {
  const authStore = useAuthStore();
  try {
    authStore.loading = true;

    const encryptKey = await getEncryptionKey();

    const newPasswordEncrypted = encryptToken(
      encryptKey.data.public_key,
      newPassword
    );

    if (!newPasswordEncrypted) {
      throw new Error("Failed to encrypt password");
    }

    await passwordResetConfirm({
      passwordResetConfirmInSchema: {
        new_password: newPasswordEncrypted,
        uid: id,
        token: token,
      },
    });

    return true;
  } catch (error) {
    handleError({
      error,
      fallbackMessage: "An error while resetting your password",
    });
  } finally {
    authStore.loading = false;
  }
};

export const useSigninWithEmail = () => {
  const router = useRouter();
  const signInWithEmail = async (data: { email: string; password: string }) => {
    const authStore = useAuthStore();
    const isValidEmail = /.+@.+\..+/.test(data.email);
    if (!isValidEmail) {
      notify({
        type: "error",
        text: "Please enter a valid email address",
      });
      return;
    }

    try {
      authStore.loading = true;

      const encryptKey = await getEncryptionKey();

      const res = await emailLogin({
        loginInputSchema: {
          email: data.email,
          password: encryptToken(
            encryptKey.data.public_key,
            data.password
          ) as string,
        },
      });

      if (res) {
        await authStore.login(res.data, true, data.email);
        return res;
      }
    } catch (error) {
      const errorData = handleError({
        error,
        silent: true,
        fallbackMessage: "Something went wrong",
      });

      if (
        errorData?.code == "4014" ||
        errorData?.message?.toLowerCase() ===
          "email has not been verified. please verify your email or reset your password"
      ) {
        setTimeout(() => {
          authStore.email = data.email;

          authStore.emailSignInErrMsg = errorData?.message;

          void router.push("/verify-signin-token");
        }, 1000);
      } else if (errorData?.code == "4001" || errorData?.code == "4005") {
        setTimeout(() => {
          void router.push("/sign-up");
        }, 1000);
      }

      notify({
        type: "error",
        text: errorData?.message,
      });
    } finally {
      authStore.loading = false;
    }
  };
  return {
    signInWithEmail,
  };
};

export const hasBusinessProperty = async () => {
  const businessStore = useBusinessStore();
  const authStore = useAuthStore();
  const tokenStore = useTokenStore();

  if (businessStore && businessStore.activeBusiness) {
    // Check if 'business' property exists in activebusiness
    const isBusinessKeyPresent = "business" in businessStore.activeBusiness;

    if (isBusinessKeyPresent) {
      requestClient.abortRequest();
      await authStore.CLEAR_X_REFRESH_TOKEN(tokenStore.token || "");
      await authStore.logout();
      requestClient.abortRequest();
      // stop all user agents connection on logoutt
    }
  }
};
